import React, { useMemo } from "react";
import type { ScriptableTemplate } from "@openreel/core";

/**
 * A thumbnail drawn from the template itself.
 *
 * Every built-in ships thumbnailUrl: null, so the gallery painted all nine as
 * the same faint gradient behind a category glyph. They were indistinguishable
 * and none of them showed what you would actually get.
 *
 * Commissioning nine images would need hosting, a CDN path, and a new one drawn
 * by hand for every template added later. This renders the template's own
 * content instead: its headline, the shape of its canvas, how much footage it
 * asks for. A new template gets a representative card the moment it exists.
 *
 * The card frame is always the same size. A portrait template shows as a
 * portrait pane INSIDE that frame rather than by stretching the card, which is
 * what used to leave the grid ragged.
 */

const ART = "https://d380el1b0agool.cloudfront.net/art";

/**
 * Backdrops, from the artwork the site already uses. Assigning them per family
 * gives every card its own image without inventing assets, and keeps the
 * gallery in the same visual world as the landing page.
 */
const BACKDROPS: Record<string, string> = {
  tiktok: `${ART}/reel-face.jpg`,
  "instagram-reels": `${ART}/reel-sun.jpg`,
  "instagram-stories": `${ART}/reel-sun.jpg`,
  "instagram-post": `${ART}/section-bg.jpg`,
  "youtube-shorts": `${ART}/reel-bird.jpg`,
  "youtube-video": `${ART}/reel-wide.jpg`,
  "lower-third": `${ART}/hero-top-bg.jpg`,
  slideshow: `${ART}/section-bg.jpg`,
  intro: `${ART}/reel-wide.jpg`,
  outro: `${ART}/reel-bird.jpg`,
  promo: `${ART}/reel-face.jpg`,
  custom: `${ART}/reel-wide.jpg`,
};

/** The headline a template leads with, if it declares one. */
function headlineOf(template: ScriptableTemplate): string | null {
  const text = template.placeholders.find(
    (p) => (p.type === "text" || p.type === "subtitle") && p.defaultValue,
  );
  return text?.defaultValue ? String(text.defaultValue) : null;
}

function slotCountOf(template: ScriptableTemplate): number {
  return template.placeholders.filter((p) => p.type === "media").length;
}

export interface TemplateThumbnailProps {
  template: ScriptableTemplate;
}

export const TemplateThumbnail: React.FC<TemplateThumbnailProps> = ({ template }) => {
  const headline = useMemo(() => headlineOf(template), [template]);
  const slots = useMemo(() => slotCountOf(template), [template]);
  const backdrop = BACKDROPS[template.socialCategory ?? "custom"] ?? BACKDROPS["custom"]!;
  const isLowerThird = template.socialCategory === "lower-third";

  const { width, height } = template.settings;
  const portrait = height > width;
  const square = height === width;

  // A long default wraps to four lines at this size and reads as a paragraph.
  const shown = headline && headline.length > 20 ? `${headline.slice(0, 19)}…` : headline;

  return (
    <div className="relative h-full w-full overflow-hidden bg-[#08080a]" aria-hidden="true">
      {/* The artwork, well behind everything. It is texture and mood, not the
          subject: at full strength it would compete with the headline, which is
          the part that actually says what the template does. */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-[0.62] transition-transform duration-500 group-hover:scale-[1.05]"
        style={{ backgroundImage: `url(${backdrop})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#08080a]/95 via-[#08080a]/45 to-[#08080a]/20" />

      {/* The template's own canvas, drawn to scale inside a fixed frame. This
          is what tells a portrait template from a landscape one now that every
          card is the same size. */}
      <div className="absolute inset-0 flex items-center justify-center p-2.5">
        <div
          className={`relative flex h-full items-center justify-center overflow-hidden rounded-[4px] ring-1 ring-white/10 ${
            portrait ? "aspect-[9/16]" : square ? "aspect-square" : "aspect-video w-full"
          }`}
          style={{ background: "rgba(8,8,10,0.28)", backdropFilter: "blur(1px)" }}
        >
          {/* Footage slots, as empty frames. Deliberately not fake imagery:
              the point is the shape of the edit, not implied content. */}
          {slots > 1 ? (
            <div className="absolute inset-0 flex gap-[2px] p-[2px]">
              {Array.from({ length: Math.min(slots, 5) }, (_, i) => (
                <div key={i} className="flex-1 rounded-[2px] bg-white/[0.05] ring-1 ring-white/[0.06]" />
              ))}
            </div>
          ) : null}

          {shown ? (
            isLowerThird ? (
              <span className="absolute bottom-2 left-2 max-w-[85%] truncate rounded-[3px] bg-white px-2 py-[3px] text-[10px] font-medium text-[#08080a]">
                {shown}
              </span>
            ) : (
              <span
                className={`relative px-2 text-center font-semibold leading-tight text-white drop-shadow-[0_1px_6px_rgba(0,0,0,0.6)] ${
                  portrait ? "text-[12px]" : "text-[15px]"
                }`}
              >
                {shown}
              </span>
            )
          ) : null}
        </div>
      </div>

      {slots > 1 ? (
        <span className="absolute right-2 top-2 rounded-full bg-black/65 px-1.5 py-[1px] text-[9px] font-medium tabular-nums text-white/80 backdrop-blur-sm">
          {slots} clips
        </span>
      ) : null}
    </div>
  );
};
